/**
 * bd-29x (U8f) + C7 fix: Legacy /admin/dbos/* → /admin/workflows/* redirects.
 *
 * Each old admin-dbos view has a new admin-workflows equivalent:
 *
 *   /admin/dbos                          → /admin/workflows
 *   /admin/dbos/queues/:queueName        → /admin/workflows/:queueName
 *                                          (queueName == workflow name)
 *   /admin/dbos/producers/:workflowName  → /admin/workflows/:workflowName
 *   /admin/dbos/runs/:workflowUuid       → /admin/workflows/cascade/:workflowUuid
 *
 * The bead description calls these "301-redirects," but in practice they
 * are CLIENT-side redirects via React Router's `<Navigate replace />`.
 * For an admin tool, that's the right level — bookmarks still resolve to
 * the new pages, and there are no SEO/cache concerns.
 *
 * # C7 fix — forward search + hash
 *
 * The original implementation built the destination from `path` only, so
 * a bookmark like `/admin/dbos/queues/foo?range=7d#cascade-root` landed on
 * `/admin/workflows/foo` with the range filter silently lost. Each
 * component now reads `useLocation()` and passes `{ pathname, search, hash }`
 * to `Navigate` so the full URL piece survives the redirect.
 *
 * These live in a tiny module (NOT inside AppRouter.tsx) so the test
 * suite can mount them via MemoryRouter without pulling in the heavy
 * App tree.
 */
import { Navigate, useLocation, useParams } from 'react-router-dom';

export function RedirectDbosQueueToWorkflow() {
  const { queueName } = useParams<{ queueName: string }>();
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: `/admin/workflows/${queueName ?? ''}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

export function RedirectDbosProducerToWorkflow() {
  const { workflowName } = useParams<{ workflowName: string }>();
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: `/admin/workflows/${workflowName ?? ''}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

export function RedirectDbosRunToCascade() {
  const { workflowUuid } = useParams<{ workflowUuid: string }>();
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: `/admin/workflows/cascade/${workflowUuid ?? ''}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}
